import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import Icon from 'src/ui/Icon';
import Item from 'src/ui/Item';
import noCalendar from '../../../../assets/img/calendar.svg';
import moment from 'moment';

// Interfaces
import { AvList } from 'src/interfaces/coachesInterface';
import { TherapyAvailabilityNewI } from 'src/interfaces/therapyInterface';

// Redux
import { setCurrentAvailability } from 'src/redux/slices/therapy.slice';

// Services
import { useTranslation } from 'react-i18next';
import {
  getMyAvailabilityUseCase,
  getTherapyNewAvailabilityUseCase,
} from 'src/services/therapy/useCases';

const AvailabilityTherapyList = (props: AvList) => {
  const { setShowDeleteAv, setIsEditing, setShowForm } = props;
  const currentRole = useAppSelector((state) => state.auth.role);
  const params = useParams<{ therapistId: string }>();
  const { t } = useTranslation(['coachAvailability']);
  const availabilities: TherapyAvailabilityNewI[] = useAppSelector(
    (state) => state.therapy.availability,
  );
  const myAvailability: TherapyAvailabilityNewI[] = useAppSelector(
    (state) => state.therapy.myAvailability,
  );
  const therapyDetail = useAppSelector((state) => state.therapy.TherapistDetail);
  const currentCoach = useAppSelector((state) => state.currentCoach.currentCoach);
  const dispatch = useAppDispatch();

  const handleDelete = (av: TherapyAvailabilityNewI) => {
    setShowForm?.(false);
    setShowDeleteAv?.(true);
    dispatch(setCurrentAvailability(av));
  };
  const handleEdit = (av: TherapyAvailabilityNewI) => {
    setIsEditing?.(true);
    setShowForm?.(true);
    dispatch(setCurrentAvailability(av));
  };

  useEffect(() => {
    if (currentRole === 'admin' && params.therapistId) {
      dispatch(getTherapyNewAvailabilityUseCase({ therapistId: params.therapistId }));
    } else if (currentRole === 'therapist' && therapyDetail.id) {
      dispatch(getMyAvailabilityUseCase());
    }
  }, [dispatch, params.therapistId, currentRole, therapyDetail.id]);

  return (
    <div className={`avList block flex-col justify-center p-4  w-2/5`}>
      <div className='availabilitiesContainer'>
        <div className={`font-semibold pb-2 text-center text-xl`}>
          {`${
            currentRole === 'admin' || currentRole === 'therapist'
              ? therapyDetail.fullName
              : currentCoach.fullName
          }${t('avHeader')}`}
        </div>
        <div className='avContainer'>
          {(currentRole === 'admin' ? availabilities : myAvailability).length === 0 && (
            <div className='flex flex-col justify-center items-center mt-20'>
              <img src={noCalendar} alt='No calendar' />
              <div className='no-availability'>{t('noAvailability')}</div>
            </div>
          )}
          {Array.isArray(currentRole === 'admin' ? availabilities : myAvailability) &&
            (currentRole === 'admin' ? availabilities : myAvailability).map(
              (av: TherapyAvailabilityNewI, i) => {
                return (
                  <Item key={`${i}-key`} itemExtraClass='cursor-default'>
                    <div className='flex items-center w-full'>
                      <div className='w-5/6 flex flex-col items-start ml-0 sm:ml-4 mr-4'>
                        <p className='text-left text-xs sm:text-base'>
                          <b className='font-medium pr-1'>{t('date')}</b>
                          {`${moment(av.startDate).format('MM/DD/YY')} - ${moment(
                            av.endDate,
                          ).format('MM/DD/YY')}`}
                        </p>
                        <p className='text-left text-xs sm:text-base'>
                          <b className='font-medium pr-1'>{t('from')}</b>
                          {moment.utc(av.startHour, 'HH:mm').format('hh:mm A')}
                          <span className='mx-0 sm:mx-1'> - </span>
                          <b className='font-medium pr-1'>{t('to')}</b>{' '}
                          {moment.utc(av.endHour, 'HH:mm').format('hh:mm A')}
                        </p>
                        {av.appointmentsCount > 0 && (
                          <div
                            className={`text-left text-xs sm:text-sm text-secondary italic font-medium`}
                          >
                            {`${t('bookedSessionsOne')} ${av.appointmentsCount} ${t(
                              'bookedSessionsTwo',
                            )}${av.appointmentsCount === 1 ? '' : 's'}`}
                          </div>
                        )}
                      </div>
                      {(currentRole === 'admin' || currentRole === 'therapist') && (
                        <div className='flex justify-center'>
                          <Icon icon='edit' onClick={() => handleEdit(av)} />
                          <Icon icon='delete' onClick={() => handleDelete(av)} />
                        </div>
                      )}
                    </div>
                  </Item>
                );
              },
            )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityTherapyList;
